/**
 * Pins administration routes to Finnish while preserving the member-facing
 * session locale held by the outer language port.
 */

import type { JSX, ReactNode } from 'react';
import { useContext, useEffect, useMemo } from 'react';

import type { LanguagePort } from '../i18n/LanguagePort.js';
import { PortsContext } from '../providers/PortsContext.js';

const FINNISH_LANGUAGE_PORT: LanguagePort = {
  active: 'fi',
  supported: ['fi'],
  set: () => undefined,
  subscribe: () => () => undefined,
};

export function AdminFinnishLanguageBoundary({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element {
  const ports = useContext(PortsContext);
  if (ports === null) {
    throw new Error('AdminFinnishLanguageBoundary must be used within a PortsProvider');
  }

  const memberLanguage = ports.language.active;
  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    document.documentElement.lang = 'fi';
    return () => {
      document.documentElement.lang = memberLanguage;
    };
  }, [memberLanguage]);

  // react-compiler-exception: PortsContext value identity must remain stable for consumer effect dependencies
  const adminPorts = useMemo(() => ({ ...ports, language: FINNISH_LANGUAGE_PORT }), [ports]);

  return <PortsContext.Provider value={adminPorts}>{children}</PortsContext.Provider>;
}
